import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  AnswerType,
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  Field,
  FieldType,
  TextInputType,
} from '@bybeam/platform-types';
import {
  ConfidentOptions,
  LikelyOptions,
  SatisfiedOptions,
  YesNoOptions,
} from '@platform-ui/application/utils/index.js';
import { eq, inList, or } from '../../ast/utils.js';
import { mapParentChildKeys } from '../../fields/dropdown.js';
import STANDARD_COMPONENTS from '../standards/index.js';
import { SpecialSectionKeys } from '../standards/types.js';
import { COMMON_PAGES } from './common.js';

type ExtractedCommonQuestionKeys = Extract<
  CommonQuestionKeys,
  | CommonQuestionKeys.ADDRESS
  | CommonQuestionKeys.MAILING_ADDRESS
  | CommonQuestionKeys.DATE_OF_BIRTH
  | CommonQuestionKeys.RACE
  | CommonQuestionKeys.LANGUAGE
  | CommonQuestionKeys.GENDER
  | CommonQuestionKeys.VETERAN
>;

const AFFIRMATION_QUESTION = STANDARD_COMPONENTS.definitions.sections[
  SpecialSectionKeys.Review
].questionGroups.find((question) => question.key === CommonQuestionKeys.AFFIRMATION);

export const COURSES = [
  'Abuse and neglect',
  'Activities of daily living',
  'Behaving professionally & ethically',
  'Body mechanics',
  'Communication skills',
  'Crisis prevention & intervention',
  'Documentation, Delegation & Reporting',
  'Emergency response (falls, incidents, fires)',
  'End-of-life care, grief',
  'Food, nutrition, and meal preparation',
  'Housekeeping',
  'Infection control and cleanliness',
  'Legal and ethical issues',
  'Managing stress',
  'Person-centered practices',
  'Physical safety and accident/injury prevention',
  'Principles of teaching & learning',
  'Problem-solving',
  'Rehabilitation and restorative care',
  'Respecting differences',
  'Role of the direct care worker',
  'Self-care',
  'Strengths- and solutions-focused practices',
  'Team building',
  'Understanding aging',
  'Understanding cultural aspects of care',
  'Understanding dementia and Alzheimer’s',
  'Understanding developmental disabilities',
  'Understanding physical disabilities',
];

const RACES = mapParentChildKeys([
  {
    value: 'AmericanIndianAndOrNativeAmericanAndOrAlaskanNative',
    label: 'American Indian/Native American/Alaskan Native',
  },
  {
    label: 'Asian',
    value: 'asian',
    children: [
      { label: 'Indian', value: 'indian' },
      { label: 'Chinese', value: 'chinese' },
      { label: 'Cambodian', value: 'cambodian' },
      { label: 'Filipino', value: 'filipino' },
      { label: 'Indonesian', value: 'indonesian' },
      { label: 'Japanese', value: 'japanese' },
      { label: 'Korean', value: 'korean' },
      { label: 'Laotian', value: 'laotian' },
      { label: 'Singaporean', value: 'singaporean' },
      { label: 'Thai', value: 'thai' },
      { label: 'Vietnamese', value: 'vietnamese' },
      {
        label: 'Not specified in list, please specify',
        value: 'other',
      },
    ],
  },
  {
    label: 'Black, African-American, or African',
    value: 'blackAndOrAfricanAmerican',
  },
  {
    label: 'Middle Eastern',
    value: 'middleEastern',
  },
  {
    value: 'nativeHawaiianOrPacificIslander',
    label: 'Native Hawaiian or other Pacific Islander',
    children: [
      { label: 'Guamanian', value: 'guamanian' },
      { label: 'Hawaiian', value: 'hawaiian' },
      { label: 'Samoan', value: 'samoan' },
      {
        label: 'Not specified in list, please specify',
        value: 'other',
      },
    ],
  },
  { value: 'whiteOrCaucasian', label: 'White/Caucasian' },
  { value: 'other', label: 'Not specified in list, please specify' },
  { value: AnswerType.Decline, label: 'Decline to state' },
]);

export const QUESTIONS: {
  [key in
    | ExtractedCommonQuestionKeys
    | 'hispanicOrLatino'
    | 'disability'
    | 'fosterYouth'
    | 'assignedGender'
    | 'experience'
    | 'areaOfStudy'
    | 'receiveProgramInfo'
    | 'reference'
    | 'sexualIdentity'
    | 'surveyForCommunityBasedProvider'
    | 'surveyForHealthCareProvider']: ApplicationQuestion;
} = {
  [CommonQuestionKeys.ADDRESS]: mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.ADDRESS],
      {
        copy: {
          title: 'What is your home address?',
          intro: 'You must live in California to participate in Caring4Cal.',
        },
        fields: [
          {
            filter: { state: 'CA' },
          },
        ],
      },
    ],
    'merge',
  ) as ApplicationQuestion,
  [CommonQuestionKeys.MAILING_ADDRESS]: mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.MAILING_ADDRESS],
      {
        copy: {
          title: 'What is your mailing address?',
          intro:
            'Your incentive check will be mailed to this address if you’re approved for payment.',
        },
        fields: [
          {
            filter: { state: 'CA' },
          },
        ],
      },
    ],
    'merge',
  ) as ApplicationQuestion,
  [CommonQuestionKeys.DATE_OF_BIRTH]:
    STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.DATE_OF_BIRTH],
  hispanicOrLatino: {
    key: 'hispanicOrLatino',
    copy: {
      title:
        'Are you Hispanic, Latino/a, or of a Spanish Origin? One or more categories may be selected.',
    },
    fields: [
      {
        type: FieldType.CheckboxGroup,
        key: 'hispanicOrLatino.hispanicOrLatino',
        options: [
          {
            value: 'mexican-mexican-american-chicano',
            label: 'Mexican, Mexican American, Chicano/a',
          },
          { value: 'puertorican', label: 'Puerto Rican' },
          {
            value: 'cuban',
            label: 'Cuban',
          },
          { value: 'other', label: 'Other Hispanic, Latino/a, or of Spanish origin (specify)' },
          { value: AnswerType.None, label: 'None of the above' },
          { value: AnswerType.Decline, label: 'Decline to state' },
        ],
        validation: { required: false },
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'hispanicOrLatino.other',
        dynamicLogic: inList('hispanicOrLatino.hispanicOrLatino', 'other'),
        copy: 'Other Hispanic, Latino/a, or of Spanish origin',
      },
    ],
  },
  [CommonQuestionKeys.RACE]: {
    key: CommonQuestionKeys.RACE,
    copy: { title: 'What is your racial/ethnic background?' },
    fields: [
      {
        type: FieldType.Dropdown,
        copy: 'Race',
        key: 'race.race',
        props: { multiple: true, childMode: 'AlwaysAvailable' },
        options: RACES,
        validation: { required: false },
      },
      {
        type: FieldType.Typography,
        key: 'race.other.label',
        copy: 'Not shown in the list (specify):',
        props: { variant: 'body', alignment: 'center' },
        dynamicLogic: or(RACES.map(({ value }) => inList('race.race', `${value}:other`))),
      },
      ...RACES.map(({ label, value }) => ({
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: `race.otherRace.${value}`,
        dynamicLogic: inList('race.race', `${value}:other`),
        copy: label,
      })),
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'race.other',
        dynamicLogic: inList('race.race', 'other'),
        copy: 'Other racial/ ethic',
      },
    ] as Field[],
  },
  [CommonQuestionKeys.LANGUAGE]: {
    key: CommonQuestionKeys.LANGUAGE,
    copy: {
      title:
        'Which of the following languages do you speak fluently/well enough to provide direct care services to clients.',
    },
    fields: [
      {
        type: FieldType.CheckboxGroup,
        key: 'language.language',
        options: [
          {
            value: 'any-indigenous-tribal-language',
            label: 'Any Indigenous and/or Tribal Language',
          },
          { value: 'any-form-of-sign-communication', label: 'Any form of Sign Communication' },
          { value: 'arabic', label: 'Arabic' },
          { value: 'armenian', label: 'Armenian' },
          { value: 'cambodian', label: 'Cambodian' },
          { value: 'chinese', label: 'Chinese' },
          { value: 'english', label: 'English' },
          { value: 'farsi', label: 'Farsi' },
          { value: 'hindi', label: 'Hindi' },
          { value: 'hmong', label: 'Hmong' },
          { value: 'japanese', label: 'Japanese' },
          { value: 'korean', label: 'Korean' },
          { value: 'laotian', label: 'Laotian' },
          { value: 'mien', label: 'Mien' },
          { value: 'punjabi', label: 'Punjabi' },
          { value: 'russian', label: 'Russian' },
          { value: 'spanish', label: 'Spanish' },
          { value: 'tagalog', label: 'Tagalog' },
          { value: 'thai', label: 'Thai' },
          { value: 'ukrainian', label: 'Ukrainian' },
          { value: 'vietnamese', label: 'Vietnamese' },
        ],
        validation: { required: false },
      },
    ],
  },
  disability: {
    key: 'disability',
    copy: { title: 'Do you have a disability?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'disability',
        options: [
          { value: 'have-disability', label: 'I have a disability' },
          { value: 'do-not-have-disability', label: 'I do not have a disability' },
          { value: AnswerType.Decline, label: 'Decline to answer' },
        ],
        validation: { required: false },
      },
    ],
  },
  [CommonQuestionKeys.VETERAN]: {
    key: CommonQuestionKeys.VETERAN,
    copy: { title: 'Are you a U.S. Veteran?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: CommonQuestionKeys.VETERAN,
        options: [
          { value: 'us-veteran', label: 'I am a U.S. Veteran' },
          { value: 'not-us-veteran', label: 'I am not a U.S. Veteran' },
          { value: AnswerType.Decline, label: 'Decline to answer' },
        ],
        validation: { required: false },
      },
    ],
  },
  fosterYouth: {
    key: 'fosterYouth',
    copy: { title: 'Are you a current or former foster youth?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'fosterYouth',
        options: [
          { value: 'foster-youth', label: 'I am a current or former foster youth' },
          { value: 'not-foster-youth', label: 'I am not a current or former foster youth' },
          { value: AnswerType.Decline, label: 'Decline to answer' },
        ],
        validation: { required: false },
      },
    ],
  },
  assignedGender: {
    key: 'assignedGender',
    copy: { title: 'What sex were you assigned at birth, on your original birth certificate?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'assignedGender',
        options: [
          { value: 'male', label: 'Male' },
          { value: 'female', label: 'Female' },
          { value: 'unknown-undetermined', label: 'Unknown/Undetermined' },
          { value: AnswerType.Decline, label: 'Decline to state' },
        ],
        validation: { required: false },
      },
    ],
  },
  [CommonQuestionKeys.GENDER]: {
    key: CommonQuestionKeys.GENDER,
    copy: { title: 'What is your gender identity?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: CommonQuestionKeys.GENDER,
        options: [
          { value: 'male', label: 'Male' },
          { value: 'female', label: 'Female' },
          { value: 'transgender', label: 'Transgender' },
          {
            value: 'not-identify-as-male-female-transgender',
            label: 'Do not identify as male, female, or transgender',
          },
          { value: AnswerType.Decline, label: 'Decline to state' },
        ],
        validation: { required: false },
      },
    ],
  },
  sexualIdentity: {
    key: 'sexualIdentity',
    copy: { title: 'Which of the option below do you most identify with?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'sexualIdentity',
        options: [
          { value: 'gay-or-lesbian', label: 'Gay or Lesbian' },
          { value: 'bisexual', label: 'Bisexual' },
          { value: 'straight', label: 'Straight or Heterosexual' },
          {
            value: 'other',
            label: 'Not specified in list',
          },
          { value: AnswerType.Decline, label: 'Decline to state' },
        ],
        validation: { required: false },
      },
    ],
  },
  experience: {
    key: 'experience',
    copy: { title: 'Which of these best describes you today?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'experience',
        options: [
          {
            value: 'new',
            label:
              'I DO NOT have a current California-issued license or certification in an [eligible job role](https://caring4cal.org/en/eligibility/#eligible)',
          },
          {
            value: 'community-based-provider',
            label:
              'I DO HAVE a current California-issued license or certification in an [eligible job role](https://caring4cal.org/en/eligibility/#eligible) and work in an [eligible job setting](https://caring4cal.org/en/eligibility/#eligible)',
          },
          {
            value: 'healthcare-provider',
            label:
              'I DO HAVE a current California-issued license or certification but DO NOT work in an [eligible job setting](https://caring4cal.org/en/eligibility/#eligible), but plan to switch to an eligible job setting',
          },
          {
            value: 'not-sure',
            label: 'I’m not sure',
          },
        ],
        validation: { required: true },
      },
    ],
  },
  areaOfStudy: {
    key: 'areaOfStudy',
    copy: { title: 'Is there an area of study you are interested in?' },
    fields: [
      {
        type: FieldType.CheckboxGroup,
        key: 'areaOfStudy.areaOfStudy',
        validation: { required: false },
        options: COURSES.map((course) => ({ label: course, value: course })),
      },
    ],
  },
  reference: {
    key: 'reference',
    copy: { title: 'How did you hear about Caring4Cal?' },
    fields: [
      {
        type: FieldType.CheckboxGroup,
        key: 'reference.reference',
        validation: { required: true },
        options: [
          {
            value: 'socialMedia',
            label: 'Social media groups or posts - Facebook, LinkedIn, Instagram, or Twitter/X',
          },
          { value: 'onlineAd', label: 'Online ad' },
          { value: 'newsletter', label: 'Newsletter' },
          { value: 'employer', label: 'Employer' },
          { value: 'trainingOrganization', label: 'Training organization' },
          { value: 'familyMemberOrFriend', label: 'Family member or friend' },
          { value: 'email', label: 'Email from Caring4Cal' },
          { value: 'text', label: 'Text from Caring4Cal' },
          { value: 'call', label: 'Call from Caring4Cal' },
          { value: 'other', label: 'Other' },
          { value: 'unknown', label: 'Don’t recall' },
        ],
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'reference.other',
        dynamicLogic: inList('reference.reference', 'other'),
        copy: 'Other',
      },
    ],
  },
  receiveProgramInfo: {
    key: 'receiveProgramInfo',
    copy: {
      title:
        'Would you like to receive Caring4Cal text messages? Coaches send a few text messages every week with resources, program information, and updates to help you during the program. Message rates may apply and you can opt out at any time.',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'receiveProgramInfo',
        options: YesNoOptions,
        validation: { required: false },
      },
    ],
  },
  surveyForCommunityBasedProvider: {
    key: 'surveyForCommunityBasedProvider',
    copy: {
      title: 'Career Perceptions',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyCBP.confident-provide-high-quality-care',
        copy: 'How confident do you feel TODAY in your ability to provide high quality care to your patients and clients?',
        options: ConfidentOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyCBP.likely-working-as-care-provider-next-12-months',
        copy: 'How likely are you to continue working as a care provider for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyCBP.likely-working-in-home-community-based-settings-next-12-months',
        copy: 'How likely are you to continue working in home- or community-based settings for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyCBP.satisfied-with-current-career',
        copy: 'How satisfied do you feel with your current career?',
        options: SatisfiedOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('experience', 'community-based-provider'),
  },
  surveyForHealthCareProvider: {
    key: 'surveyForHealthCareProvider',
    copy: {
      title: 'Career Perceptions',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'surveyHCP.confident-provide-high-quality-care',
        copy: 'How confident do you feel TODAY in your ability to provide high quality care to your patients and clients?',
        options: ConfidentOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyHCP.likely-working-as-care-provider-next-12-months',
        copy: 'How likely are you to continue working as a care provider for the next 12 months?',
        options: LikelyOptions,
        validation: { required: false },
      },
      {
        type: FieldType.RadioList,
        key: 'surveyHCP.satisfied-with-current-career',
        copy: 'How satisfied do you feel with your current career?',
        options: SatisfiedOptions,
        validation: { required: false },
      },
    ],
    dynamicLogic: eq('experience', 'healthcare-provider'),
  },
};
const COMMON_SECTIONS: ApplicationSection[] = [
  {
    key: 'requiredInfo',
    name: 'Required Information',
    overview: {
      description:
        'In this section, we will ask you questions to help us review your future incentive applications. These questions are required.',
      title: 'Required Information',
    },
    questionGroups: [
      {
        key: 'requiredInfo',
        name: 'Required Information',
        questions: [
          QUESTIONS[CommonQuestionKeys.ADDRESS],
          QUESTIONS[CommonQuestionKeys.MAILING_ADDRESS],
          QUESTIONS[CommonQuestionKeys.DATE_OF_BIRTH],
        ],
      },
    ],
  },
  {
    key: 'demographicInfo',
    name: 'Demographic Information',
    overview: {
      description:
        'In this section, we will ask you some questions that we will use to broadly understand overall who is participating in the Caring4Cal program. We do not share this information. You have the option to to decline answering any of these questions.',
      title: 'Demographic Information',
    },
    questionGroups: [
      {
        key: 'demographicInfo',
        name: 'Demographic Information',
        questions: [
          QUESTIONS.hispanicOrLatino,
          QUESTIONS[CommonQuestionKeys.RACE],
          QUESTIONS[CommonQuestionKeys.LANGUAGE],
          QUESTIONS.disability,
          QUESTIONS[CommonQuestionKeys.VETERAN],
          QUESTIONS.fosterYouth,
          QUESTIONS.assignedGender,
          QUESTIONS[CommonQuestionKeys.GENDER],
          QUESTIONS.sexualIdentity,
          QUESTIONS.experience,
          QUESTIONS.areaOfStudy,
          QUESTIONS.reference,
          QUESTIONS.receiveProgramInfo,
        ],
      },
      {
        key: 'careerPerceptions',
        name: 'Career Perceptions',
        dynamicLogic: or([
          eq('experience', 'community-based-provider'),
          eq('experience', 'healthcare-provider'),
        ]),
        overview: {
          title: 'Career Perceptions',
          description:
            'We’ve got just a few more quick questions. We’d like to know your thoughts about your career as a care provider in home- and community-based settings.',
        },
        questions: [
          QUESTIONS.surveyForCommunityBasedProvider,
          QUESTIONS.surveyForHealthCareProvider,
        ],
      },
    ],
  },
  mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.sections[SpecialSectionKeys.Review],
      {
        overview: {
          description: `
  Next you will review your registration, Please make sure all the information you provided is correct. You can change or update any of your answers in this step.`,
          title: 'Review your registration',
        },
        questionGroups: [
          {
            key: 'submission.assistance',
            name: 'Application Assistance',
            questions: [
              {
                ...STANDARD_COMPONENTS.definitions.questions[
                  CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER
                ],
                copy: {
                  title: 'Did someone help you fill out this application?',
                  intro:
                    'Please provide the following information about the person who helped you complete your registration. If nobody helped you, click SKIP.',
                },
              },
            ],
          },
          AFFIRMATION_QUESTION,
        ],
      } as ApplicationSection,
    ],
    'merge',
  ),
];

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'welcome',
      title: 'Welcome to Caring4Cal!',
      body: `
Congratulations on taking the first step. Caring4Cal helps you accelerate your career, develop your skills, and provide quality care to older adults and people with disabilities. 

After you have completed your short registration, you will come back here to claim your incentives. 

For now, let’s set up your account!`,
    },
    {
      key: 'application-process',
      layout: 'steps',
      title: 'About the registration process. Here are some things to know before you start.',
      steps: [
        {
          title: 'It takes up to 10 minutes to complete',
          icon: 'time',
          description: 'You can expect to spend 10 minutes or less to complete your registration.',
        },
        {
          title: 'Your progress will be saved as you go',
          icon: 'checkCircleOutline',
        },
        {
          title: 'Enter the necessary information to set up your account',
          icon: 'infoOutline',
          description: 'We recommend completing the registration in a safe and private place.',
        },

        {
          title: 'Your data is secure',
          icon: 'verifiedOutlined',
          description:
            'We will safely store your data to help you apply for incentives faster. We will not share your data with third parties without your consent.',
        },
        {
          title: 'Check your inbox',
          icon: 'mail',
          description:
            'We will send you a welcome email to let you know we received your registration.',
        },
      ],
    },
    COMMON_PAGES.termsOfServiceAndPrivacyPolicy,
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: '',
        message: `
Your first step in participating in the Caring4Cal program is to complete your Caring4Cal registration form. Start your registration by clicking “START NEW APPLICATION” below.
          
When you are ready to take courses, you can find them [here](https://www.caring4cal.org/en/home-care-provider-training/).`,
      },
      {
        status: 'InProgress',
        message: `
You’ve made great progress so far! Keep working on your Caring4Cal registration so you can claim your Caring4Cal incentives!
          
When you are ready to take courses, you can find them [here](https://www.caring4cal.org/en/home-care-provider-training/).`,
      },
      {
        status: 'Incomplete',
        label: 'Continue Registration',
        message: `
You’ve made great progress so far! Keep working on your Caring4Cal registration so you can claim your Caring4Cal incentives!
          
When you are ready to take courses, you can find them [here](https://www.caring4cal.org/en/home-care-provider-training/).`,
      },
      {
        status: 'AwaitingReview',
        label: 'Status: Registered',
        message:
          'Thank you for registering for Caring4Cal! Come back here when you are ready to submit incentive claims.',
      },
      {
        status: 'Approved',
        label: 'Status: Registered',
        message:
          'You are now registered for Caring4Cal! Come back here when you are ready to submit an incentive claim.',
      },
    ],
    submission: {
      title: 'What happens next?',
      bullets: [
        {
          key: 'applyForIncentives',
          title: 'Apply for incentives!',
          icon: 'description',
          description:
            'Once Caring4Cal courses are available and you have taken at least 15 hours of Caring4Cal training, come back here to claim your incentives.',
        },
      ],
    },
  },
  sections: COMMON_SECTIONS,
};
export default CONFIG;
